/**
 * Partner Program — public landing page.
 *
 * 3 tier cards: Reseller (40%), Partner (50%), Elite (blurred, up to 60%)
 * Early Partner slots counter + CTA
 * FAQ section
 * 11 languages
 * Responsive: desktop / tablet / mobile
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../i18n';
import PublicNav from '../components/PublicNav';

// ── Breakpoints ──────────────────────────────────────────────────────────────
function useWindowWidth() {
    const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    useEffect(() => {
        const h = () => setW(window.innerWidth);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);
    return w;
}

// ── Multilingual content ─────────────────────────────────────────────────────
const CONTENT = {
    en: {
        heroLabel: 'RESELLER PROGRAM',
        heroTitle: 'Grow with us.\nEarn more at every level.',
        heroSub: "Transparent pricing, no private deals. The more you sell, the more you save. It's that simple.",
        // Tier cards
        tiers: [
            {
                name: 'Reseller',
                discount: '40%',
                color: '#10b981',
                badge: '🟢',
                price: 'off public rates',
                features: ['Instant activation', 'Full API access', 'All services available', 'Standard support', 'Deposit bonuses'],
                cta: 'Start now',
                sub: 'Free · No minimum volume',
            },
            {
                name: 'Partner',
                discount: '50%',
                color: '#8b5cf6',
                badge: '🟣',
                price: 'off public rates',
                features: ['Everything in Reseller', 'Priority delivery queue', 'Dedicated support channel', 'Monthly volume reports', 'Early access to new services'],
                cta: 'Unlock Partner',
                sub: '€500/month or Early Partner slot',
                popular: true,
            },
            {
                name: 'Elite',
                discount: 'up to 60%',
                color: '#f59e0b',
                badge: '⚫',
                price: 'off public rates',
                features: ['Personalized discount rate', 'Direct line to founders', 'Exclusive priority queue', 'Custom service configuration', 'First access to new regions'],
                cta: 'By invitation only',
                sub: 'High weekly volume · Reviewed weekly',
                secret: true,
            },
        ],
        // How it works
        howTitle: 'How it works',
        howSteps: [
            { icon: '1', title: 'Register', desc: 'Create your free reseller account. Instant activation, no approval needed.' },
            { icon: '2', title: 'Start selling', desc: 'Connect via API or use the dashboard. You get 40% off on every order from day one.' },
            { icon: '3', title: 'Unlock Partner', desc: 'Reach €500/month in orders and automatically upgrade to 50% discount.' },
        ],
        // Early Partner
        earlyTitle: 'Early Partner Program',
        earlySub: "Every month we open 3 exclusive Partner slots. Register, make your first deposit within 7 days, and get Partner tier (50% discount) for 3 full months — no volume required.",
        earlySlots: 'slots available this month',
        earlyCta: 'Claim your slot',
        earlyNote: 'Slots reset on the 1st of each month. First come, first served.',
        // Downgrade rules
        rulesTitle: 'Fair rules, no surprises',
        rules: [
            { icon: '📈', title: 'Instant upgrades', desc: 'Hit the volume threshold? Your tier upgrades immediately. No waiting.' },
            { icon: '🛡️', title: '2-month grace period', desc: "Had a slow month? No problem. You keep your tier for 2 full months before any downgrade." },
            { icon: '🔄', title: 'Easy recovery', desc: "Dropped a tier? Just hit the volume again and you're back — instantly." },
            { icon: '🎁', title: 'Deposit bonuses stack', desc: '+3% on €100 · +5% on €500 · +10% on €1,000 — on top of your tier discount.' },
        ],
        // FAQ
        faqTitle: 'Frequently asked questions',
        faqs: [
            { q: 'Can I lose my tier?', a: 'Only after 2 consecutive months below the volume threshold. You always have time to recover.' },
            { q: 'What happens if I have a slow month?', a: "Nothing. You have a 2-month grace period. Your tier stays the same." },
            { q: 'Can I get Partner back after a downgrade?', a: 'Yes, instantly. As soon as your monthly volume reaches €500 again.' },
            { q: 'Do deposit bonuses stack with tier discounts?', a: "Yes! They're completely independent. Partner (50% off) + €1,000 deposit (+10% bonus) = maximum savings." },
            { q: "What's the Elite tier?", a: "Our Elite program is by invitation only for high-volume partners. The discount is personalized (up to 60%) and reviewed weekly. If you qualify, we'll reach out personally." },
            { q: 'How is volume calculated?', a: 'Total spend on completed and active orders in the last 30 rolling days. Trial credit and refunded orders are excluded.' },
            { q: 'Can I connect via API?', a: 'Yes! Standard SMM panel API format. Works with PerfectPanel and all major platforms.' },
        ],
        // Final CTA
        ctaTitle: 'Ready to start earning?',
        ctaSub: 'Join hundreds of resellers growing their business with OrbitGrowth.',
        ctaBtn: 'Create free account',
    },
    it: {
        heroLabel: 'PROGRAMMA RESELLER',
        heroTitle: 'Cresci con noi.\nGuadagna di più a ogni livello.',
        heroSub: 'Prezzi trasparenti, nessun accordo privato. Più vendi, più risparmi. Semplice.',
        tiers: [
            {
                name: 'Reseller',
                discount: '40%',
                color: '#10b981',
                badge: '🟢',
                price: 'sui prezzi pubblici',
                features: ['Attivazione istantanea', 'Accesso API completo', 'Tutti i servizi disponibili', 'Supporto standard', 'Bonus deposito'],
                cta: 'Inizia ora',
                sub: 'Gratis · Nessun volume minimo',
            },
            {
                name: 'Partner',
                discount: '50%',
                color: '#8b5cf6',
                badge: '🟣',
                price: 'sui prezzi pubblici',
                features: ['Tutto del Reseller', 'Coda di consegna prioritaria', 'Canale supporto dedicato', 'Report volume mensili', 'Accesso anticipato ai nuovi servizi'],
                cta: 'Sblocca Partner',
                sub: '€500/mese o slot Early Partner',
                popular: true,
            },
            {
                name: 'Elite',
                discount: 'fino al 60%',
                color: '#f59e0b',
                badge: '⚫',
                price: 'sui prezzi pubblici',
                features: ['Sconto personalizzato', 'Linea diretta con i fondatori', 'Coda prioritaria esclusiva', 'Configurazione servizi custom', 'Primo accesso a nuove regioni'],
                cta: 'Solo su invito',
                sub: 'Alti volumi settimanali · Revisione settimanale',
                secret: true,
            },
        ],
        howTitle: 'Come funziona',
        howSteps: [
            { icon: '1', title: 'Registrati', desc: 'Crea il tuo account reseller gratuito. Attivazione istantanea, nessuna approvazione.' },
            { icon: '2', title: 'Inizia a vendere', desc: 'Connettiti via API o usa la dashboard. 40% di sconto su ogni ordine dal primo giorno.' },
            { icon: '3', title: 'Sblocca Partner', desc: 'Raggiungi €500/mese in ordini e passa automaticamente allo sconto del 50%.' },
        ],
        earlyTitle: 'Programma Early Partner',
        earlySub: 'Ogni mese apriamo 3 slot Partner esclusivi. Registrati, fai il primo deposito entro 7 giorni, e ottieni il tier Partner (50% di sconto) per 3 mesi — senza requisiti di volume.',
        earlySlots: 'slot disponibili questo mese',
        earlyCta: 'Reclama il tuo slot',
        earlyNote: 'Gli slot si resettano il 1° di ogni mese. Primo arrivato, primo servito.',
        rulesTitle: 'Regole chiare, nessuna sorpresa',
        rules: [
            { icon: '📈', title: 'Upgrade istantanei', desc: 'Raggiungi la soglia? Il tier sale subito. Nessuna attesa.' },
            { icon: '🛡️', title: '2 mesi di grazia', desc: 'Mese scarso? Nessun problema. Mantieni il tier per 2 mesi prima di qualsiasi downgrade.' },
            { icon: '🔄', title: 'Recupero facile', desc: 'Sei sceso di tier? Raggiungi il volume e torni subito — istantaneamente.' },
            { icon: '🎁', title: 'I bonus deposito si sommano', desc: '+3% su €100 · +5% su €500 · +10% su €1.000 — oltre allo sconto tier.' },
        ],
        faqTitle: 'Domande frequenti',
        faqs: [
            { q: 'Posso perdere il mio tier?', a: 'Solo dopo 2 mesi consecutivi sotto la soglia di volume. Hai sempre tempo per recuperare.' },
            { q: 'Cosa succede se ho un mese scarso?', a: 'Niente. Hai 2 mesi di grazia. Il tuo tier resta invariato.' },
            { q: 'Posso tornare Partner dopo un downgrade?', a: 'Sì, istantaneamente. Appena il tuo volume mensile raggiunge di nuovo €500.' },
            { q: 'I bonus deposito si sommano allo sconto tier?', a: 'Sì! Sono completamente indipendenti. Partner (50%) + deposito €1.000 (+10% bonus) = massimo risparmio.' },
            { q: "Cos'è il tier Elite?", a: 'Il nostro programma Elite è solo su invito per partner ad alto volume. Lo sconto è personalizzato (fino al 60%) e viene rivisto settimanalmente. Se ti qualifichi, ti contatteremo personalmente.' },
            { q: 'Come viene calcolato il volume?', a: 'Spesa totale su ordini completati e attivi negli ultimi 30 giorni rolling. Credito trial e ordini rimborsati sono esclusi.' },
            { q: 'Posso connettermi via API?', a: 'Sì! Formato API panel SMM standard. Funziona con PerfectPanel e tutte le piattaforme principali.' },
        ],
        ctaTitle: 'Pronto a iniziare?',
        ctaSub: 'Unisciti ai reseller che fanno crescere il loro business con OrbitGrowth.',
        ctaBtn: 'Crea account gratuito',
    },
    es: {
        heroLabel: 'PROGRAMA RESELLER',
        heroTitle: 'Crece con nosotros.\nGana más en cada nivel.',
        heroSub: 'Precios transparentes, sin acuerdos privados. Cuanto más vendas, más ahorras. Así de simple.',
        tiers: [
            { name: 'Reseller', discount: '40%', color: '#10b981', badge: '🟢', price: 'sobre precios públicos', features: ['Activación instantánea', 'Acceso API completo', 'Todos los servicios', 'Soporte estándar', 'Bonos por depósito'], cta: 'Empezar ahora', sub: 'Gratis · Sin volumen mínimo' },
            { name: 'Partner', discount: '50%', color: '#8b5cf6', badge: '🟣', price: 'sobre precios públicos', features: ['Todo de Reseller', 'Cola de entrega prioritaria', 'Canal de soporte dedicado', 'Reportes mensuales', 'Acceso anticipado a nuevos servicios'], cta: 'Desbloquear Partner', sub: '€500/mes o slot Early Partner', popular: true },
            { name: 'Elite', discount: 'hasta 60%', color: '#f59e0b', badge: '⚫', price: 'sobre precios públicos', features: ['Descuento personalizado', 'Línea directa con fundadores', 'Cola prioritaria exclusiva', 'Configuración de servicios custom', 'Primer acceso a nuevas regiones'], cta: 'Solo por invitación', sub: 'Alto volumen semanal · Revisión semanal', secret: true },
        ],
        howTitle: 'Cómo funciona',
        howSteps: [
            { icon: '1', title: 'Regístrate', desc: 'Crea tu cuenta reseller gratis. Activación instantánea.' },
            { icon: '2', title: 'Empieza a vender', desc: 'Conecta via API o usa el panel. 40% de descuento desde el primer día.' },
            { icon: '3', title: 'Desbloquea Partner', desc: 'Alcanza €500/mes en pedidos y sube automáticamente al 50%.' },
        ],
        earlyTitle: 'Programa Early Partner',
        earlySub: 'Cada mes abrimos 3 slots Partner exclusivos. Regístrate, deposita en 7 días, y obtén Partner (50%) por 3 meses — sin requisitos de volumen.',
        earlySlots: 'slots disponibles este mes', earlyCta: 'Reclama tu slot',
        earlyNote: 'Los slots se reinician el 1 de cada mes.',
        rulesTitle: 'Reglas claras', rules: [
            { icon: '📈', title: 'Ascensos instantáneos', desc: '¿Alcanzaste el umbral? Tu tier sube al instante.' },
            { icon: '🛡️', title: '2 meses de gracia', desc: '¿Mes flojo? Sin problema. Tu tier se mantiene 2 meses.' },
            { icon: '🔄', title: 'Recuperación fácil', desc: '¿Bajaste de tier? Alcanza el volumen y vuelves al instante.' },
            { icon: '🎁', title: 'Bonos acumulables', desc: '+3% en €100 · +5% en €500 · +10% en €1.000 — además del descuento tier.' },
        ],
        faqTitle: 'Preguntas frecuentes', faqs: [
            { q: '¿Puedo perder mi tier?', a: 'Solo después de 2 meses consecutivos bajo el umbral.' },
            { q: '¿Qué pasa si tengo un mes flojo?', a: 'Nada. Tienes 2 meses de gracia.' },
            { q: '¿Puedo recuperar Partner?', a: 'Sí, al instante cuando alcanzas €500/mes de nuevo.' },
            { q: '¿Los bonos se suman al descuento?', a: 'Sí, son independientes.' },
            { q: '¿Qué es el tier ???', a: 'Nuestro programa Elite es solo por invitación para partners de alto volumen.' },
            { q: '¿Cómo se calcula el volumen?', a: 'Gasto total en pedidos de los últimos 30 días.' },
            { q: '¿Puedo conectar por API?', a: 'Sí, formato estándar de panel SMM.' },
        ],
        ctaTitle: '¿Listo para empezar?', ctaSub: 'Únete a los resellers que crecen con OrbitGrowth.', ctaBtn: 'Crear cuenta gratis',
    },
    de: {
        heroLabel: 'RESELLER-PROGRAMM',
        heroTitle: 'Wachse mit uns.\nVerdiene mehr auf jeder Stufe.',
        heroSub: 'Transparente Preise, keine privaten Deals. Je mehr du verkaufst, desto mehr sparst du.',
        tiers: [
            { name: 'Reseller', discount: '40%', color: '#10b981', badge: '🟢', price: 'auf öffentliche Preise', features: ['Sofortige Aktivierung', 'Voller API-Zugang', 'Alle Services verfügbar', 'Standard-Support', 'Einzahlungsboni'], cta: 'Jetzt starten', sub: 'Kostenlos · Kein Mindestvolumen' },
            { name: 'Partner', discount: '50%', color: '#8b5cf6', badge: '🟣', price: 'auf öffentliche Preise', features: ['Alles von Reseller', 'Prioritäts-Warteschlange', 'Dedizierter Support', 'Monatliche Berichte', 'Früher Zugang zu neuen Services'], cta: 'Partner freischalten', sub: '€500/Monat oder Early Partner Slot', popular: true },
            { name: 'Elite', discount: 'bis zu 60%', color: '#f59e0b', badge: '⚫', price: 'auf öffentliche Preise', features: ['Personalisierter Rabatt', 'Direkter Draht zu Gründern', 'Exklusive Prioritätswarteschlange', 'Individuelle Service-Konfiguration', 'Erster Zugang zu neuen Regionen'], cta: 'Nur auf Einladung', sub: 'Hohes Wochenvolumen · Wöchentliche Überprüfung', secret: true },
        ],
        howTitle: 'So funktioniert es',
        howSteps: [
            { icon: '1', title: 'Registrieren', desc: 'Erstelle dein kostenloses Reseller-Konto. Sofortige Aktivierung.' },
            { icon: '2', title: 'Verkaufen', desc: 'API oder Dashboard. 40% Rabatt ab Tag 1.' },
            { icon: '3', title: 'Partner freischalten', desc: 'Erreiche €500/Monat und steige automatisch auf 50%.' },
        ],
        earlyTitle: 'Early Partner Programm',
        earlySub: 'Jeden Monat öffnen wir 3 exklusive Partner-Slots. Registriere dich, zahle in 7 Tagen ein, und erhalte Partner (50%) für 3 Monate.',
        earlySlots: 'Slots diesen Monat verfügbar', earlyCta: 'Slot sichern',
        earlyNote: 'Slots werden am 1. jedes Monats zurückgesetzt.',
        rulesTitle: 'Faire Regeln', rules: [
            { icon: '📈', title: 'Sofortige Upgrades', desc: 'Schwelle erreicht? Sofortiger Aufstieg.' },
            { icon: '🛡️', title: '2 Monate Gnadenfrist', desc: 'Schwacher Monat? Dein Tier bleibt 2 Monate erhalten.' },
            { icon: '🔄', title: 'Einfache Rückkehr', desc: 'Abgestiegen? Erreiche das Volumen und du bist sofort zurück.' },
            { icon: '🎁', title: 'Boni stapeln sich', desc: '+3% bei €100 · +5% bei €500 · +10% bei €1.000.' },
        ],
        faqTitle: 'Häufige Fragen', faqs: [
            { q: 'Kann ich mein Tier verlieren?', a: 'Nur nach 2 aufeinanderfolgenden Monaten unter der Schwelle.' },
            { q: 'Was passiert bei einem schwachen Monat?', a: 'Nichts. Du hast 2 Monate Gnadenfrist.' },
            { q: 'Kann ich Partner zurückbekommen?', a: 'Ja, sofort bei €500/Monat.' },
            { q: 'Stapeln sich Einzahlungsboni?', a: 'Ja, sie sind unabhängig vom Tier-Rabatt.' },
            { q: 'Was ist das ??? Tier?', a: 'Unser Elite-Programm ist nur auf Einladung.' },
            { q: 'Wie wird das Volumen berechnet?', a: 'Gesamtausgaben der letzten 30 Tage.' },
            { q: 'Kann ich per API verbinden?', a: 'Ja, Standard-SMM-Panel-Format.' },
        ],
        ctaTitle: 'Bereit zu starten?', ctaSub: 'Werde Teil der OrbitGrowth-Reseller.', ctaBtn: 'Kostenloses Konto erstellen',
    },
    fr: {
        heroLabel: 'PROGRAMME REVENDEUR',
        heroTitle: 'Grandissez avec nous.\nGagnez plus à chaque niveau.',
        heroSub: 'Prix transparents, pas de deals privés. Plus vous vendez, plus vous économisez.',
        tiers: [
            { name: 'Reseller', discount: '40%', color: '#10b981', badge: '🟢', price: 'sur les prix publics', features: ['Activation instantanée', 'Accès API complet', 'Tous les services', 'Support standard', 'Bonus de dépôt'], cta: 'Commencer', sub: 'Gratuit · Pas de volume minimum' },
            { name: 'Partner', discount: '50%', color: '#8b5cf6', badge: '🟣', price: 'sur les prix publics', features: ['Tout de Reseller', 'File de livraison prioritaire', 'Support dédié', 'Rapports mensuels', 'Accès anticipé aux nouveaux services'], cta: 'Débloquer Partner', sub: '€500/mois ou slot Early Partner', popular: true },
            { name: 'Elite', discount: "jusqu'à 60%", color: '#f59e0b', badge: '⚫', price: 'sur les prix publics', features: ['Remise personnalisée', 'Ligne directe avec les fondateurs', 'File prioritaire exclusive', 'Configuration services sur mesure', 'Premier accès aux nouvelles régions'], cta: 'Sur invitation uniquement', sub: 'Haut volume hebdomadaire · Révision hebdomadaire', secret: true },
        ],
        howTitle: 'Comment ça marche',
        howSteps: [
            { icon: '1', title: "S'inscrire", desc: 'Créez votre compte revendeur gratuit. Activation instantanée.' },
            { icon: '2', title: 'Vendre', desc: 'API ou tableau de bord. 40% de réduction dès le premier jour.' },
            { icon: '3', title: 'Débloquer Partner', desc: "Atteignez €500/mois et passez automatiquement à 50%." },
        ],
        earlyTitle: 'Programme Early Partner',
        earlySub: 'Chaque mois, 3 slots Partner exclusifs. Inscrivez-vous, déposez en 7 jours, et obtenez Partner (50%) pour 3 mois.',
        earlySlots: 'slots disponibles ce mois', earlyCta: 'Réclamer votre slot',
        earlyNote: 'Les slots sont réinitialisés le 1er de chaque mois.',
        rulesTitle: 'Règles équitables', rules: [
            { icon: '📈', title: 'Montée instantanée', desc: 'Seuil atteint ? Upgrade immédiat.' },
            { icon: '🛡️', title: '2 mois de grâce', desc: 'Mois faible ? Votre tier reste pendant 2 mois.' },
            { icon: '🔄', title: 'Récupération facile', desc: 'Descendu ? Atteignez le volume et vous remontez.' },
            { icon: '🎁', title: 'Bonus cumulables', desc: '+3% à €100 · +5% à €500 · +10% à €1.000.' },
        ],
        faqTitle: 'Questions fréquentes', faqs: [
            { q: 'Puis-je perdre mon tier ?', a: 'Seulement après 2 mois consécutifs sous le seuil.' },
            { q: "Que se passe-t-il si j'ai un mois faible ?", a: 'Rien. Vous avez 2 mois de grâce.' },
            { q: 'Puis-je retrouver Partner ?', a: 'Oui, instantanément à €500/mois.' },
            { q: 'Les bonus se cumulent ?', a: 'Oui, indépendants du tier.' },
            { q: "C'est quoi le tier ??? ?", a: 'Notre programme Elite est sur invitation uniquement.' },
            { q: 'Comment le volume est calculé ?', a: 'Dépenses des 30 derniers jours.' },
            { q: 'Connexion API possible ?', a: 'Oui, format standard SMM.' },
        ],
        ctaTitle: 'Prêt à commencer ?', ctaSub: 'Rejoignez les revendeurs OrbitGrowth.', ctaBtn: 'Créer un compte gratuit',
    },
    ru: {
        heroLabel: 'ПРОГРАММА РЕСЕЛЛЕРОВ',
        heroTitle: 'Растите вместе с нами.\nЗарабатывайте больше на каждом уровне.',
        heroSub: 'Прозрачные цены, никаких частных сделок. Чем больше продаёте — тем больше экономите.',
        tiers: [
            { name: 'Reseller', discount: '40%', color: '#10b981', badge: '🟢', price: 'от публичных цен', features: ['Мгновенная активация', 'Полный доступ к API', 'Все сервисы', 'Стандартная поддержка', 'Бонусы за депозит'], cta: 'Начать', sub: 'Бесплатно · Без минимума' },
            { name: 'Partner', discount: '50%', color: '#8b5cf6', badge: '🟣', price: 'от публичных цен', features: ['Всё из Reseller', 'Приоритетная доставка', 'Выделенная поддержка', 'Ежемесячные отчёты', 'Ранний доступ к новым сервисам'], cta: 'Разблокировать', sub: '€500/мес или Early Partner слот', popular: true },
            { name: 'Elite', discount: 'до 60%', color: '#f59e0b', badge: '⚫', price: 'от публичных цен', features: ['Персональная скидка', 'Прямая связь с основателями', 'Эксклюзивная приоритетная очередь', 'Индивидуальная настройка сервисов', 'Первый доступ к новым регионам'], cta: 'Только по приглашению', sub: 'Высокие недельные объёмы · Еженедельная проверка', secret: true },
        ],
        howTitle: 'Как это работает', howSteps: [
            { icon: '1', title: 'Регистрация', desc: 'Создайте бесплатный аккаунт реселлера.' },
            { icon: '2', title: 'Продавайте', desc: 'API или панель. 40% скидка с первого дня.' },
            { icon: '3', title: 'Станьте Partner', desc: 'Достигните €500/мес и получите 50% автоматически.' },
        ],
        earlyTitle: 'Программа Early Partner', earlySub: 'Каждый месяц 3 эксклюзивных слота Partner. Зарегистрируйтесь, внесите депозит за 7 дней и получите Partner (50%) на 3 месяца.',
        earlySlots: 'слотов доступно', earlyCta: 'Занять слот', earlyNote: 'Слоты обновляются 1-го числа каждого месяца.',
        rulesTitle: 'Честные правила', rules: [
            { icon: '📈', title: 'Мгновенное повышение', desc: 'Достигли порога — повышение сразу.' },
            { icon: '🛡️', title: '2 месяца отсрочки', desc: 'Слабый месяц? Tier сохраняется 2 месяца.' },
            { icon: '🔄', title: 'Лёгкое восстановление', desc: 'Упали? Достигните объём — и вы вернулись.' },
            { icon: '🎁', title: 'Бонусы складываются', desc: '+3% от €100 · +5% от €500 · +10% от €1.000.' },
        ],
        faqTitle: 'Частые вопросы', faqs: [
            { q: 'Могу ли я потерять tier?', a: 'Только после 2 месяцев подряд ниже порога.' },
            { q: 'Что будет при слабом месяце?', a: 'Ничего. 2 месяца отсрочки.' },
            { q: 'Можно вернуть Partner?', a: 'Да, мгновенно при €500/мес.' },
            { q: 'Бонусы суммируются?', a: 'Да, независимо от tier.' },
            { q: 'Что за tier ???', a: 'Elite — только по приглашению.' },
            { q: 'Как считается объём?', a: 'Расходы за последние 30 дней.' },
            { q: 'Есть API?', a: 'Да, стандартный формат SMM.' },
        ],
        ctaTitle: 'Готовы начать?', ctaSub: 'Присоединяйтесь к реселлерам OrbitGrowth.', ctaBtn: 'Создать аккаунт',
    },
    pt: {
        heroLabel: 'PROGRAMA RESELLER',
        heroTitle: 'Cresça conosco.\nGanhe mais a cada nível.',
        heroSub: 'Preços transparentes, sem acordos privados. Quanto mais vende, mais economiza.',
        tiers: [
            { name: 'Reseller', discount: '40%', color: '#10b981', badge: '🟢', price: 'sobre preços públicos', features: ['Ativação instantânea', 'Acesso API completo', 'Todos os serviços', 'Suporte padrão', 'Bônus de depósito'], cta: 'Começar agora', sub: 'Grátis · Sem volume mínimo' },
            { name: 'Partner', discount: '50%', color: '#8b5cf6', badge: '🟣', price: 'sobre preços públicos', features: ['Tudo do Reseller', 'Fila de entrega prioritária', 'Suporte dedicado', 'Relatórios mensais', 'Acesso antecipado'], cta: 'Desbloquear Partner', sub: '€500/mês ou slot Early Partner', popular: true },
            { name: 'Elite', discount: 'até 60%', color: '#f59e0b', badge: '⚫', price: 'sobre preços públicos', features: ['Desconto personalizado', 'Linha direta com fundadores', 'Fila prioritária exclusiva', 'Configuração de serviços custom', 'Primeiro acesso a novas regiões'], cta: 'Apenas por convite', sub: 'Alto volume semanal · Revisão semanal', secret: true },
        ],
        howTitle: 'Como funciona', howSteps: [
            { icon: '1', title: 'Registre-se', desc: 'Crie sua conta reseller gratuita.' },
            { icon: '2', title: 'Comece a vender', desc: 'API ou painel. 40% de desconto desde o dia 1.' },
            { icon: '3', title: 'Desbloqueie Partner', desc: 'Alcance €500/mês e suba para 50%.' },
        ],
        earlyTitle: 'Programa Early Partner', earlySub: 'Todo mês abrimos 3 slots exclusivos. Registre-se, deposite em 7 dias e ganhe Partner (50%) por 3 meses.',
        earlySlots: 'slots disponíveis', earlyCta: 'Garantir slot', earlyNote: 'Slots reiniciam no dia 1.',
        rulesTitle: 'Regras justas', rules: [
            { icon: '📈', title: 'Upgrades instantâneos', desc: 'Atingiu o limite? Sobe na hora.' },
            { icon: '🛡️', title: '2 meses de carência', desc: 'Mês fraco? Seu tier fica por 2 meses.' },
            { icon: '🔄', title: 'Recuperação fácil', desc: 'Caiu? Alcance o volume e volta.' },
            { icon: '🎁', title: 'Bônus acumulam', desc: '+3% em €100 · +5% em €500 · +10% em €1.000.' },
        ],
        faqTitle: 'Perguntas frequentes', faqs: [
            { q: 'Posso perder meu tier?', a: 'Só após 2 meses consecutivos abaixo do limite.' },
            { q: 'E se tiver um mês fraco?', a: 'Nada. 2 meses de carência.' },
            { q: 'Posso recuperar Partner?', a: 'Sim, instantaneamente a €500/mês.' },
            { q: 'Bônus acumulam?', a: 'Sim, independentes do tier.' },
            { q: 'O que é o tier ???', a: 'Programa Elite, apenas por convite.' },
            { q: 'Como o volume é calculado?', a: 'Gastos dos últimos 30 dias.' },
            { q: 'Tem API?', a: 'Sim, formato padrão SMM.' },
        ],
        ctaTitle: 'Pronto para começar?', ctaSub: 'Junte-se aos resellers OrbitGrowth.', ctaBtn: 'Criar conta grátis',
    },
    tr: {
        heroLabel: 'BAYİ PROGRAMI',
        heroTitle: 'Bizimle büyüyün.\nHer seviyede daha fazla kazanın.',
        heroSub: 'Şeffaf fiyatlar, özel anlaşma yok. Ne kadar satarsanız, o kadar tasarruf edersiniz.',
        tiers: [
            { name: 'Reseller', discount: '%40', color: '#10b981', badge: '🟢', price: 'halka açık fiyatlarda', features: ['Anında aktivasyon', 'Tam API erişimi', 'Tüm hizmetler', 'Standart destek', 'Yatırım bonusu'], cta: 'Şimdi başla', sub: 'Ücretsiz · Minimum yok' },
            { name: 'Partner', discount: '%50', color: '#8b5cf6', badge: '🟣', price: 'halka açık fiyatlarda', features: ['Reseller dahil', 'Öncelikli teslimat', 'Özel destek', 'Aylık raporlar', 'Erken erişim'], cta: 'Partner aç', sub: '€500/ay veya Early Partner slotu', popular: true },
            { name: 'Elite', discount: '%60\'a kadar', color: '#f59e0b', badge: '⚫', price: 'halka açık fiyatlarda', features: ['Kişiselleştirilmiş indirim', 'Kurucularla doğrudan iletişim', 'Özel öncelik kuyruğu', 'Özel hizmet yapılandırması', 'Yeni bölgelere ilk erişim'], cta: 'Sadece davetiye ile', sub: 'Yüksek haftalık hacim · Haftalık inceleme', secret: true },
        ],
        howTitle: 'Nasıl çalışır', howSteps: [
            { icon: '1', title: 'Kaydol', desc: 'Ücretsiz bayi hesabı oluştur.' },
            { icon: '2', title: 'Satışa başla', desc: 'API veya panel. İlk günden %40 indirim.' },
            { icon: '3', title: 'Partner ol', desc: '€500/ay\'a ulaş ve otomatik %50\'ye geç.' },
        ],
        earlyTitle: 'Early Partner', earlySub: 'Her ay 3 özel Partner slotu. Kaydol, 7 günde yatır, 3 ay Partner (50%) kazan.',
        earlySlots: 'slot mevcut', earlyCta: 'Slotunu al', earlyNote: 'Slotlar her ayın 1\'inde sıfırlanır.',
        rulesTitle: 'Adil kurallar', rules: [
            { icon: '📈', title: 'Anında yükselme', desc: 'Eşiğe ulaştın? Hemen yükselirsin.' },
            { icon: '🛡️', title: '2 ay tolerans', desc: 'Zayıf ay? Tier\'in 2 ay korunur.' },
            { icon: '🔄', title: 'Kolay geri dönüş', desc: 'Düştün mü? Hacme ulaş ve geri dön.' },
            { icon: '🎁', title: 'Bonuslar birikir', desc: '+%3 €100 · +%5 €500 · +%10 €1.000.' },
        ],
        faqTitle: 'Sık sorulan sorular', faqs: [
            { q: 'Tier\'imi kaybedebilir miyim?', a: 'Sadece art arda 2 ay eşiğin altında kalırsan.' },
            { q: 'Zayıf ay olursa?', a: '2 ay tolerans var.' },
            { q: 'Partner\'a geri dönebilir miyim?', a: 'Evet, €500/ay\'da anında.' },
            { q: 'Bonuslar eklenir mi?', a: 'Evet, bağımsız.' },
            { q: '??? tier nedir?', a: 'Elite programı sadece davetle.' },
            { q: 'Hacim nasıl hesaplanır?', a: 'Son 30 günün harcamaları.' },
            { q: 'API var mı?', a: 'Evet, standart SMM formatı.' },
        ],
        ctaTitle: 'Başlamaya hazır mısın?', ctaSub: 'OrbitGrowth bayilerine katıl.', ctaBtn: 'Ücretsiz hesap oluştur',
    },
    ja: {
        heroLabel: 'リセラープログラム',
        heroTitle: '一緒に成長しましょう。\nレベルごとにもっと稼ぎましょう。',
        heroSub: '透明な価格設定、プライベートな取引なし。売れば売るほど、節約できます。',
        tiers: [
            { name: 'Reseller', discount: '40%', color: '#10b981', badge: '🟢', price: '公開価格より', features: ['即時有効化', '完全なAPIアクセス', '全サービス利用可能', '標準サポート', '入金ボーナス'], cta: '今すぐ開始', sub: '無料・最低数量なし' },
            { name: 'Partner', discount: '50%', color: '#8b5cf6', badge: '🟣', price: '公開価格より', features: ['Resellerの全て', '優先配信キュー', '専用サポート', '月次レポート', '新サービス早期アクセス'], cta: 'Partnerを解除', sub: '€500/月またはEarly Partnerスロット', popular: true },
            { name: 'Elite', discount: '最大60%', color: '#f59e0b', badge: '⚫', price: '公開価格より', features: ['パーソナライズされた割引率', '創業者への直通ライン', '専用優先キュー', 'カスタムサービス設定', '新地域への先行アクセス'], cta: '招待制のみ', sub: '高い週間ボリューム · 毎週レビュー', secret: true },
        ],
        howTitle: '仕組み', howSteps: [
            { icon: '1', title: '登録', desc: '無料のリセラーアカウントを作成。即時有効化。' },
            { icon: '2', title: '販売開始', desc: 'APIまたはダッシュボード。初日から40%オフ。' },
            { icon: '3', title: 'Partner解除', desc: '€500/月に到達すると自動的に50%に。' },
        ],
        earlyTitle: 'Early Partnerプログラム', earlySub: '毎月3つの限定Partnerスロット。登録して7日以内に入金すれば、3ヶ月間Partner（50%）を獲得。',
        earlySlots: 'スロット利用可能', earlyCta: 'スロットを確保', earlyNote: 'スロットは毎月1日にリセットされます。',
        rulesTitle: '公正なルール', rules: [
            { icon: '📈', title: '即時アップグレード', desc: '閾値到達？即座にアップグレード。' },
            { icon: '🛡️', title: '2ヶ月の猶予期間', desc: '低調な月？2ヶ月間ティアを維持。' },
            { icon: '🔄', title: '簡単な回復', desc: 'ダウングレード？ボリュームを達成すれば即復帰。' },
            { icon: '🎁', title: 'ボーナス累積', desc: '+3% €100 · +5% €500 · +10% €1,000。' },
        ],
        faqTitle: 'よくある質問', faqs: [
            { q: 'ティアを失うことはありますか？', a: '閾値を2ヶ月連続で下回った場合のみ。' },
            { q: '低調な月はどうなりますか？', a: '何も起きません。2ヶ月の猶予があります。' },
            { q: 'Partnerに戻れますか？', a: 'はい、€500/月で即座に。' },
            { q: 'ボーナスは累積しますか？', a: 'はい、独立しています。' },
            { q: '???ティアとは？', a: 'Elite招待制プログラムです。' },
            { q: 'ボリュームの計算方法は？', a: '過去30日間の支出合計。' },
            { q: 'API接続は可能？', a: 'はい、標準SMM形式。' },
        ],
        ctaTitle: '始める準備はできましたか？', ctaSub: 'OrbitGrowthリセラーに参加しましょう。', ctaBtn: '無料アカウント作成',
    },
    ar: {
        heroLabel: 'برنامج الموزعين',
        heroTitle: 'انمو معنا.\nاكسب أكثر في كل مستوى.',
        heroSub: 'أسعار شفافة، بدون صفقات خاصة. كلما بعت أكثر، وفرت أكثر.',
        tiers: [
            { name: 'Reseller', discount: '40%', color: '#10b981', badge: '🟢', price: 'من الأسعار العامة', features: ['تفعيل فوري', 'وصول كامل للـ API', 'جميع الخدمات', 'دعم عادي', 'مكافآت الإيداع'], cta: 'ابدأ الآن', sub: 'مجاناً · بدون حد أدنى' },
            { name: 'Partner', discount: '50%', color: '#8b5cf6', badge: '🟣', price: 'من الأسعار العامة', features: ['كل ما في Reseller', 'أولوية التسليم', 'دعم مخصص', 'تقارير شهرية', 'وصول مبكر للخدمات الجديدة'], cta: 'فتح Partner', sub: '€500/شهر أو فتحة Early Partner', popular: true },
            { name: 'Elite', discount: 'حتى 60%', color: '#f59e0b', badge: '⚫', price: 'من الأسعار العامة', features: ['خصم مخصص', 'خط مباشر مع المؤسسين', 'أولوية حصرية', 'تكوين خدمات مخصص', 'أول وصول للمناطق الجديدة'], cta: 'بدعوة فقط', sub: 'حجم أسبوعي عالي · مراجعة أسبوعية', secret: true },
        ],
        howTitle: 'كيف يعمل', howSteps: [
            { icon: '1', title: 'سجل', desc: 'أنشئ حساب موزع مجاني.' },
            { icon: '2', title: 'ابدأ البيع', desc: 'API أو لوحة التحكم. خصم 40% من اليوم الأول.' },
            { icon: '3', title: 'افتح Partner', desc: 'اوصل €500/شهر واحصل على 50% تلقائياً.' },
        ],
        earlyTitle: 'برنامج Early Partner', earlySub: 'كل شهر نفتح 3 فتحات Partner حصرية. سجل، أودع خلال 7 أيام، واحصل على Partner (50%) لمدة 3 أشهر.',
        earlySlots: 'فتحات متاحة', earlyCta: 'احجز فتحتك', earlyNote: 'تُعاد الفتحات في أول كل شهر.',
        rulesTitle: 'قواعد عادلة', rules: [
            { icon: '📈', title: 'ترقية فورية', desc: 'وصلت للحد؟ ترقية فورية.' },
            { icon: '🛡️', title: 'شهران مهلة', desc: 'شهر ضعيف؟ مستواك يبقى شهرين.' },
            { icon: '🔄', title: 'استرداد سهل', desc: 'نزلت؟ اوصل للحجم وعد فوراً.' },
            { icon: '🎁', title: 'المكافآت تتراكم', desc: '+3% على €100 · +5% على €500 · +10% على €1,000.' },
        ],
        faqTitle: 'أسئلة شائعة', faqs: [
            { q: 'هل يمكن أن أخسر مستواي؟', a: 'فقط بعد شهرين متتاليين تحت الحد.' },
            { q: 'ماذا لو كان شهري ضعيفاً؟', a: 'لا شيء. لديك شهران مهلة.' },
            { q: 'هل يمكن استعادة Partner؟', a: 'نعم، فوراً عند €500/شهر.' },
            { q: 'هل المكافآت تتراكم؟', a: 'نعم، مستقلة.' },
            { q: 'ما هو مستوى ???؟', a: 'برنامج Elite بدعوة فقط.' },
            { q: 'كيف يُحسب الحجم؟', a: 'إجمالي الإنفاق في آخر 30 يوماً.' },
            { q: 'هل يوجد API؟', a: 'نعم، صيغة SMM قياسية.' },
        ],
        ctaTitle: 'مستعد للبدء؟', ctaSub: 'انضم إلى موزعي OrbitGrowth.', ctaBtn: 'إنشاء حساب مجاني',
    },
    zh: {
        heroLabel: '经销商计划',
        heroTitle: '与我们共同成长。\n每个级别赚取更多。',
        heroSub: '透明定价，没有私下交易。卖得越多，省得越多。',
        tiers: [
            { name: 'Reseller', discount: '40%', color: '#10b981', badge: '🟢', price: '公开价格', features: ['即时激活', '完整API访问', '所有服务可用', '标准支持', '存款奖金'], cta: '立即开始', sub: '免费 · 无最低量' },
            { name: 'Partner', discount: '50%', color: '#8b5cf6', badge: '🟣', price: '公开价格', features: ['包含Reseller全部', '优先交付队列', '专属支持', '月度报告', '新服务早期访问'], cta: '解锁Partner', sub: '€500/月或Early Partner名额', popular: true },
            { name: 'Elite', discount: '最高60%', color: '#f59e0b', badge: '⚫', price: '公开价格', features: ['个性化折扣率', '直接联系创始人', '专属优先队列', '自定义服务配置', '新地区优先访问'], cta: '仅限邀请', sub: '高周交易量 · 每周审核', secret: true },
        ],
        howTitle: '运作方式', howSteps: [
            { icon: '1', title: '注册', desc: '创建免费经销商账户。即时激活。' },
            { icon: '2', title: '开始销售', desc: 'API或面板。第一天起40%折扣。' },
            { icon: '3', title: '解锁Partner', desc: '达到€500/月自动升级到50%。' },
        ],
        earlyTitle: 'Early Partner计划', earlySub: '每月开放3个独家Partner名额。注册，7天内存款，获得Partner（50%）3个月。',
        earlySlots: '个名额可用', earlyCta: '领取名额', earlyNote: '名额每月1日重置。',
        rulesTitle: '公平规则', rules: [
            { icon: '📈', title: '即时升级', desc: '达到门槛？立即升级。' },
            { icon: '🛡️', title: '2个月宽限期', desc: '低迷月？等级保留2个月。' },
            { icon: '🔄', title: '轻松恢复', desc: '降级了？达到量即刻恢复。' },
            { icon: '🎁', title: '奖金叠加', desc: '+3% €100 · +5% €500 · +10% €1,000。' },
        ],
        faqTitle: '常见问题', faqs: [
            { q: '会失去等级吗？', a: '仅在连续2个月低于门槛后。' },
            { q: '低迷月怎么办？', a: '没事，有2个月宽限期。' },
            { q: '能恢复Partner吗？', a: '可以，达到€500/月即刻恢复。' },
            { q: '奖金叠加吗？', a: '是的，独立计算。' },
            { q: '???等级是什么？', a: 'Elite计划，仅限邀请。' },
            { q: '如何计算交易量？', a: '过去30天的总支出。' },
            { q: '有API吗？', a: '有，标准SMM格式。' },
        ],
        ctaTitle: '准备开始了吗？', ctaSub: '加入OrbitGrowth经销商。', ctaBtn: '创建免费账户',
    },
};

// ── Styles ───────────────────────────────────────────────────────────────────
const s = {
    root: { minHeight: '100vh', background: '#050810', color: '#f1f5f9', fontFamily: '"Segoe UI", system-ui, sans-serif' },
    section: { maxWidth: 1200, margin: '0 auto', padding: '80px 24px' },
    sectionAlt: { background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' },
    // Hero
    hero: { textAlign: 'center', paddingTop: 120, paddingBottom: 80, maxWidth: 800, margin: '0 auto', padding: '120px 24px 80px' },
    heroBadge: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', background: 'rgba(29,78,216,0.15)', border: '1px solid rgba(29,78,216,0.3)', borderRadius: 100, fontSize: 13, color: '#60a5fa', marginBottom: 24, fontWeight: 700, letterSpacing: '1px' },
    heroTitle: { fontSize: 52, fontWeight: 900, lineHeight: 1.1, marginBottom: 20, letterSpacing: '-1.5px', whiteSpace: 'pre-line' },
    heroSub: { fontSize: 18, color: '#94a3b8', lineHeight: 1.7, maxWidth: 600, margin: '0 auto' },
    // Tier cards
    tiersGrid: { display: 'grid', gap: 24, maxWidth: 1100, margin: '0 auto', padding: '0 24px' },
    tierCard: { position: 'relative', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '40px 32px', textAlign: 'center', transition: 'transform 0.2s, border-color 0.2s' },
    tierPopular: { border: '2px solid #8b5cf6', transform: 'scale(1.03)', background: 'rgba(139,92,246,0.05)' },
    tierSecret: { border: '1px solid rgba(245,158,11,0.4)', background: 'linear-gradient(145deg, rgba(245,158,11,0.06), rgba(0,0,0,0.3))', overflow: 'hidden', position: 'relative', boxShadow: '0 0 40px rgba(245,158,11,0.08), inset 0 1px 0 rgba(245,158,11,0.15)' },
    tierName: { fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: 8 },
    tierDiscount: { fontSize: 56, fontWeight: 900, letterSpacing: '-2px', lineHeight: 1, marginBottom: 4 },
    tierPrice: { fontSize: 14, color: '#64748b', marginBottom: 24 },
    tierFeature: { fontSize: 14, color: '#94a3b8', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'left' },
    tierFeatureSecret: { fontSize: 14, color: 'rgba(245,158,11,0.4)', padding: '8px 0', borderBottom: '1px solid rgba(245,158,11,0.08)', textAlign: 'left', filter: 'blur(2px)', userSelect: 'none' },
    tierCta: { display: 'inline-block', width: '100%', padding: '14px 0', borderRadius: 12, fontWeight: 700, fontSize: 15, cursor: 'pointer', border: 'none', marginTop: 24, transition: 'opacity 0.2s' },
    tierSub: { fontSize: 12, color: '#64748b', marginTop: 10 },
    popularBadge: { position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)', background: '#8b5cf6', color: '#fff', padding: '5px 20px', borderRadius: '0 0 12px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' },
    // How it works
    howGrid: { display: 'grid', gap: 32, maxWidth: 900, margin: '40px auto 0' },
    howStep: { display: 'flex', gap: 20, alignItems: 'flex-start' },
    howIcon: { width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg,#1d4ed8,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 900, color: '#fff', flexShrink: 0 },
    howTitle2: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
    howDesc: { fontSize: 14, color: '#94a3b8', lineHeight: 1.6 },
    // Early Partner
    earlyBox: { maxWidth: 700, margin: '0 auto', textAlign: 'center', background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(29,78,216,0.1))', border: '1px solid rgba(139,92,246,0.25)', borderRadius: 24, padding: '48px 32px' },
    earlyCounter: { fontSize: 64, fontWeight: 900, letterSpacing: '-2px', lineHeight: 1 },
    earlyLabel: { fontSize: 16, color: '#94a3b8', marginTop: 8 },
    // Rules
    rulesGrid: { display: 'grid', gap: 20, maxWidth: 900, margin: '40px auto 0' },
    ruleCard: { display: 'flex', gap: 16, alignItems: 'flex-start', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: '24px' },
    ruleIcon: { fontSize: 28, flexShrink: 0 },
    ruleTitle: { fontSize: 16, fontWeight: 700, marginBottom: 4 },
    ruleDesc: { fontSize: 14, color: '#94a3b8', lineHeight: 1.5 },
    // FAQ
    faqItem: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '20px 24px', cursor: 'pointer', transition: 'border-color 0.2s' },
    faqQ: { fontSize: 15, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    faqA: { fontSize: 14, color: '#94a3b8', lineHeight: 1.6, marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' },
    // CTA
    ctaBox: { textAlign: 'center', maxWidth: 600, margin: '0 auto' },
    ctaTitle2: { fontSize: 36, fontWeight: 900, letterSpacing: '-1px', marginBottom: 12 },
    ctaSub2: { fontSize: 16, color: '#94a3b8', marginBottom: 32 },
    ctaBtn: { display: 'inline-block', padding: '16px 48px', background: 'linear-gradient(135deg,#1d4ed8,#06b6d4)', color: '#fff', border: 'none', borderRadius: 14, fontWeight: 800, fontSize: 16, cursor: 'pointer', textDecoration: 'none' },
    // Shared
    sectionTitle: { fontSize: 36, fontWeight: 900, textAlign: 'center', letterSpacing: '-1px', marginBottom: 16 },
    sectionSub: { fontSize: 16, color: '#94a3b8', textAlign: 'center', maxWidth: 600, margin: '0 auto 40px', lineHeight: 1.6 },
};

// ── FAQ item component ───────────────────────────────────────────────────────
function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div style={{ ...s.faqItem, borderColor: open ? 'rgba(29,78,216,0.3)' : undefined }} onClick={() => setOpen(o => !o)}>
            <div style={s.faqQ}>{q} <span style={{ fontSize: 18, color: '#64748b', transition: 'transform 0.2s', transform: open ? 'rotate(45deg)' : 'none' }}>+</span></div>
            {open && <div style={s.faqA}>{a}</div>}
        </div>
    );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function Partner() {
    const { lang } = useLang();
    const navigate = useNavigate();
    const w = useWindowWidth();
    const c = CONTENT[lang] || CONTENT.en;

    const isMobile = w <= 600;
    const isTablet = w <= 900;

    // Early Partner slots — fetched from API (real count based on deterministic schedule + real claims)
    const [earlySlots, setEarlySlots] = useState({ total: 3, claimed: 0, available: 3 });
    useEffect(() => {
        fetch('/public/partner-slots')
            .then(r => r.json())
            .then(data => { if (data.total) setEarlySlots(data); })
            .catch(() => {});
    }, []);

    return (
        <div style={s.root}>
            <PublicNav />

            {/* ── Hero ── */}
            <div style={{ ...s.hero, paddingLeft: isMobile ? 16 : 24, paddingRight: isMobile ? 16 : 24 }}>
                <div style={s.heroBadge}>{c.heroLabel}</div>
                <h1 style={{ ...s.heroTitle, fontSize: isMobile ? 32 : isTablet ? 40 : 52 }}>{c.heroTitle}</h1>
                <p style={s.heroSub}>{c.heroSub}</p>
            </div>

            {/* ── Tier Cards ── */}
            <div style={{ ...s.tiersGrid, gridTemplateColumns: isTablet ? '1fr' : 'repeat(3, 1fr)', maxWidth: isTablet ? 480 : 1100 }}>
                {c.tiers.map((tier, i) => (
                    <div key={i} style={{
                        ...s.tierCard,
                        ...(tier.popular ? s.tierPopular : {}),
                        ...(tier.secret ? s.tierSecret : {}),
                    }}>
                        {tier.popular && <div style={s.popularBadge}>BEST VALUE</div>}
                        <div style={{ ...s.tierName, color: tier.color }}>{tier.badge} {tier.name}</div>
                        {tier.secret ? (
                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 8, color: '#f59e0b' }}>
                                <span style={{ fontSize: 14, fontWeight: 500, color: 'rgba(245,158,11,0.5)' }}>up to</span>
                                <span style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-2px', lineHeight: 1 }}>60%</span>
                            </div>
                        ) : (
                            <div style={{ ...s.tierDiscount, color: '#f1f5f9' }}>{tier.discount}</div>
                        )}
                        <div style={s.tierPrice}>{tier.price}</div>
                        <div style={{ marginBottom: 8 }}>
                            {tier.features.map((f, j) => (
                                <div key={j} style={tier.secret ? s.tierFeatureSecret : s.tierFeature}>
                                    {!tier.secret && <span style={{ color: tier.color, marginRight: 8 }}>✓</span>}
                                    {f}
                                </div>
                            ))}
                        </div>
                        <button
                            style={{
                                ...s.tierCta,
                                background: tier.secret ? 'rgba(245,158,11,0.15)' : (tier.popular ? '#8b5cf6' : 'linear-gradient(135deg,#1d4ed8,#06b6d4)'),
                                color: tier.secret ? '#f59e0b' : '#fff',
                                cursor: tier.secret ? 'default' : 'pointer',
                            }}
                            onClick={() => !tier.secret && navigate('/register')}
                        >
                            {tier.cta}
                        </button>
                        <div style={s.tierSub}>{tier.sub}</div>

                        {/* Secret tier overlay scanlines */}
                        {tier.secret && (
                            <div style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 20,
                                background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(245,158,11,0.03) 2px, rgba(245,158,11,0.03) 4px)',
                                pointerEvents: 'none',
                            }} />
                        )}
                    </div>
                ))}
            </div>

            {/* ── How it works ── */}
            <div style={{ ...s.sectionAlt, marginTop: 80 }}>
                <div style={s.section}>
                    <h2 style={s.sectionTitle}>{c.howTitle}</h2>
                    <div style={{ ...s.howGrid, gridTemplateColumns: isTablet ? '1fr' : 'repeat(3, 1fr)' }}>
                        {c.howSteps.map((step, i) => (
                            <div key={i} style={{ ...s.howStep, flexDirection: isTablet ? 'row' : 'column', alignItems: isTablet ? 'flex-start' : 'center', textAlign: isTablet ? 'left' : 'center' }}>
                                <div style={s.howIcon}>{step.icon}</div>
                                <div>
                                    <div style={s.howTitle2}>{step.title}</div>
                                    <div style={s.howDesc}>{step.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Early Partner ── */}
            <div style={s.section}>
                <h2 style={s.sectionTitle}>{c.earlyTitle}</h2>
                <p style={s.sectionSub}>{c.earlySub}</p>
                <div style={s.earlyBox}>
                    <div style={{ ...s.earlyCounter, background: 'linear-gradient(135deg,#8b5cf6,#06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {earlySlots.available}/{earlySlots.total}
                    </div>
                    <div style={s.earlyLabel}>{c.earlySlots}</div>
                    <button
                        style={{ ...s.ctaBtn, marginTop: 24 }}
                        onClick={() => navigate('/register')}
                    >
                        {c.earlyCta}
                    </button>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 16 }}>{c.earlyNote}</div>
                </div>
            </div>

            {/* ── Rules ── */}
            <div style={s.sectionAlt}>
                <div style={s.section}>
                    <h2 style={s.sectionTitle}>{c.rulesTitle}</h2>
                    <div style={{ ...s.rulesGrid, gridTemplateColumns: isTablet ? '1fr' : 'repeat(2, 1fr)' }}>
                        {c.rules.map((rule, i) => (
                            <div key={i} style={s.ruleCard}>
                                <div style={s.ruleIcon}>{rule.icon}</div>
                                <div>
                                    <div style={s.ruleTitle}>{rule.title}</div>
                                    <div style={s.ruleDesc}>{rule.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── FAQ ── */}
            <div style={s.section}>
                <h2 style={s.sectionTitle}>{c.faqTitle}</h2>
                <div style={{ maxWidth: 700, margin: '40px auto 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {c.faqs.map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} />)}
                </div>
            </div>

            {/* ── Final CTA ── */}
            <div style={s.sectionAlt}>
                <div style={{ ...s.section, ...s.ctaBox }}>
                    <h2 style={{ ...s.ctaTitle2, fontSize: isMobile ? 28 : 36 }}>{c.ctaTitle}</h2>
                    <p style={s.ctaSub2}>{c.ctaSub}</p>
                    <button style={s.ctaBtn} onClick={() => navigate('/register')}>{c.ctaBtn}</button>
                </div>
            </div>
        </div>
    );
}
